import React, { useEffect, useRef } from 'react';

import { usePartnerLinkingImage } from 'microsites/partner-linking/use-partner-linking-images';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import TimHortonsLogo from 'components/icons/tim-hortons-logo';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${Styles.color.background};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Styles.color.primary};
  // Match the main app nav bar (height + red) so the microsite header is
  // consistent with the rest of the site.
  height: ${Styles.layout.navHeight.mobile};
  padding: 0 1rem;

  ${Styles.desktop`
    height: ${Styles.layout.navHeight.desktop};
  `}

  svg {
    height: 1.5rem;
    width: auto;

    // TH brand logo is red by default; invert to white over the red banner.
    path {
      fill: ${Styles.color.white};
    }
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  // Top-aligned below the header, never centered (Figma Partnerships 1377:61836).
  justify-content: flex-start;
  text-align: center;
  padding: 2rem 1rem;
  margin: 0 auto;
  width: 100%;
  max-width: 24rem;

  ${Styles.desktop`
    max-width: ${Styles.layout.smallerSectionMaxWidth};
    padding: 6.5rem 1rem 3rem;
  `}
`;

// Illustration comes from the featurePartnerLinking singleton in Sanity.
const IllustrationContainer = styled.div`
  margin-bottom: 1rem;

  img {
    width: 12.5rem;
    height: 12.5rem;
  }
`;

const Title = styled.h1`
  color: ${Styles.color.black};
  font-family: ${Styles.fontFamily.base};
  font-size: 1.5rem;
  line-height: 2rem;
  margin: 0.5rem 0;

  ${Styles.desktop`
    font-size: 2rem;
    line-height: 2.5rem;
  `}

  // Suppressed for the programmatic mount focus; kept for keyboard users.
  outline: none;

  &:focus-visible {
    outline: 2px solid ${Styles.color.tertiary};
    outline-offset: 2px;
  }
`;

const Description = styled.p`
  // Figma Tokens/secondary-1 — the design system token isn't wired into Styles.
  color: #654d4d;
  font-size: 1rem;
  line-height: 1.5rem;
  margin: 0 0 2rem 0;
`;

const CtaWrapper = styled.div`
  // Hidden on mobile: guests reach this screen from the app and can return there
  // natively, so the design omits the CTA.
  display: none;
  width: 100%;
  max-width: 20rem;

  ${Styles.desktop`
    display: block;
  `}
`;

interface IRegionUnavailableProps {
  onBackToApp: () => void;
}

const RegionUnavailable: React.FC<IRegionUnavailableProps> = ({ onBackToApp }) => {
  const { formatMessage } = useIntl();
  const { imageUrl: regionUnavailableImageUrl } = usePartnerLinkingImage(
    'regionUnavailableIllustration'
  );
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Replaces the loading state in place, so there is no route change for
  // assistive tech to announce.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <Page data-testid="ct-region-unavailable">
      <Helmet title={formatMessage({ id: 'ctLinkingRegionUnavailableTitle' })} />
      <Header>
        <TimHortonsLogo />
      </Header>
      <Content>
        <IllustrationContainer aria-hidden>
          {regionUnavailableImageUrl && <img src={regionUnavailableImageUrl} alt="" />}
        </IllustrationContainer>
        <Title ref={headingRef} tabIndex={-1}>
          {formatMessage({ id: 'ctLinkingRegionUnavailableTitle' })}
        </Title>
        <Description>{formatMessage({ id: 'ctLinkingRegionUnavailableDescription' })}</Description>
        <CtaWrapper>
          <ActionButton fullWidth onClick={onBackToApp}>
            {formatMessage({ id: 'ctLinkingRegionUnavailableBackToAppCta' })}
          </ActionButton>
        </CtaWrapper>
      </Content>
    </Page>
  );
};

export default RegionUnavailable;
